import React, { useMemo } from 'react';
import { CircularProgress, TableCell, TableRow } from '@mui/material';
import QcsAlert from './common/basic/QcsAlert';
import { GridLoading } from './common/grid/GridLoading';
import { GridError } from './common/grid/GridError';
import { GridEmpty } from './common/grid/GridEmpty';
import { useResourceLoading } from '@api/utils/ApiLoadingStore';
import { useResourceError } from '@api/utils/ApiMessagesStore';
import { Resource } from '@api/resources';
import { QcsTableBody } from './common/basic/QcsTableBody';
import { QcsTable } from './common/basic/QcsTable';
import { useTranslation } from 'react-i18next';

interface ShowContentProps<T = any> {
  resource: Resource | Resource[];
  data: T[] | T | null | undefined;
  children: React.ReactNode;

  /**
   * Custom function to determine if data is empty
   * Default: checks if array length is 0, or if data is null/undefined
   */
  isEmpty?: (data: T[] | T | null | undefined) => boolean;
}

export const ShowQcsTableContent = <T,>({ resource, data, children, isEmpty }: ShowContentProps<T>) => {
  const { t } = useTranslation();
  const isLoading = useResourceLoading(resource);
  const errors = useResourceError(resource, false);

  // Check if data is empty using custom function or default logic
  const dataIsEmpty = isEmpty ? isEmpty(data) : Array.isArray(data) ? data.length === 0 : data === null || data === undefined;

  // Find column count by searching for QcsTableHead
  const colCount = useMemo(() => {
    let count = 0;

    const findColCount = (node: React.ReactNode): void => {
      React.Children.forEach(node, (child) => {
        if (count > 0) return; // Already found

        if (React.isValidElement(child)) {
          const childType = child.type as any;
          const typeName = childType?.displayName || childType?.name || '';

          if (typeName === 'QcsTableHead') {
            // Count columns from the header row
            React.Children.forEach((child as any).props?.children, (row: any) => {
              if (React.isValidElement(row)) {
                count = React.Children.count((row as any).props?.children || []);
              }
            });
          } else if ((child as any).props?.children) {
            findColCount((child as any).props.children);
          }
        }
      });
    };

    findColCount(children);
    return count;
  }, [children]);

  // Helper to create a message tbody
  const createMessageBody = (message: React.ReactNode) => (
    <QcsTableBody key="message-body">
      <TableRow>
        <TableCell colSpan={colCount} sx={{ padding: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{message}</div>
        </TableCell>
      </TableRow>
    </QcsTableBody>
  );

  // Transform children: keep thead, replace tbody with message when needed
  const transformChildren = (node: React.ReactNode, message?: React.ReactNode): React.ReactNode => {
    return React.Children.map(node, (child) => {
      if (!React.isValidElement(child)) return child;

      const childType = child.type as any;
      const typeName = childType?.displayName || childType?.name || '';

      // Keep QcsTableHead as-is
      if (typeName === 'QcsTableHead') {
        return child;
      }

      // Replace QcsTableBody or SortableContext with message tbody
      if (typeName === 'QcsTableBody' || typeName === 'SortableContext') {
        return message ? createMessageBody(message) : child;
      }

      // For QcsTable, ensure it has full width
      if (typeName === 'QcsTable') {
        const originalStyle = (child as any).props?.style || {};
        return React.cloneElement(
          child,
          { style: { ...originalStyle, width: '100%' } } as any,
          transformChildren((child as any).props.children, message)
        );
      }

      // Recursively transform children
      if ((child as any).props?.children) {
        return React.cloneElement(child, {}, transformChildren((child as any).props.children, message));
      }

      return child;
    });
  };

  return (
    <QcsTable>
      {(() => {
        // Check if we have a table structure (colCount > 0 means we found a header)
        if (colCount > 0) {
          if (isLoading) {
            return transformChildren(children, <CircularProgress />);
          }

          if (errors.length > 0) {
            return transformChildren(children, <QcsAlert severity="error">{t('common.tableError')}</QcsAlert>);
          }

          if (dataIsEmpty) {
            return transformChildren(children, <QcsAlert severity="info">{t('common.tableEmpty')}</QcsAlert>);
          }
        } else {
          // No table head found, use grid components
          if (isLoading) return <GridLoading />;
          if (errors.length > 0) return <GridError />;
          if (dataIsEmpty) return <GridEmpty />;
        }

        // Normal state: render children as-is
        return children;
      })()}
    </QcsTable>
  );
};
